/**
 * Copy multi-idioma para el ingreso custom free-join
 * (atraer → Clerk gratis → perfil → link grupo free).
 */

import type { InvitacionExclusivaMuxLocale } from "@/lib/invitacionExclusivaMuxCopy";
import {
  invitacionExclusivaMuxLocaleFromCountry,
  invitacionExclusivaMuxLocaleFromLanguage,
} from "@/lib/invitacionExclusivaMuxCopy";

export type FreeJoinEntryCopy = {
  welcomeEyebrow: string;
  welcomeTitle: (personName: string) => string;
  welcomeBody: string;
  benefit1: string;
  benefit2: string;
  benefit3: string;
  notVipNote: string;
  continueCta: string;
  accountTitle: string;
  accountBody: string;
  accountPrivacy: string;
  createAccessTab: string;
  haveAccountTab: string;
  /** Tras cuenta: ir a completar perfil. */
  nextProfileHint: string;
  closeLabel: string;
  stepOf: (n: number, total: number) => string;
};

const BY_LOCALE: Record<InvitacionExclusivaMuxLocale, FreeJoinEntryCopy> = {
  es: {
    welcomeEyebrow: "Acceso gratis",
    welcomeTitle: (n) => `Registrate gratis con ${n}`,
    welcomeBody:
      "Creá tu cuenta gratis, completá un perfil cortito y recién ahí te muestro el link del grupo free.",
    benefit1: "Cuenta gratis en segundos",
    benefit2: "Perfil simple (alias + Telegram + edad)",
    benefit3: "Después ves el link del grupo free",
    notVipNote: "No es el plan VIP de pago — es el acceso free de la comunidad.",
    continueCta: "Quiero registrarme gratis",
    accountTitle: "Creá tu acceso gratis",
    accountBody:
      "Con tu email te guardo el lugar. Después completás el perfil y te abro el grupo free.",
    accountPrivacy: "Solo para tu acceso. Sin spam.",
    createAccessTab: "Crear acceso",
    haveAccountTab: "Ya tengo cuenta",
    nextProfileHint: "Siguiente: completar perfil → ver link del grupo",
    closeLabel: "Cerrar",
    stepOf: (n, t) => `Paso ${n} de ${t}`,
  },
  en: {
    welcomeEyebrow: "Free access",
    welcomeTitle: (n) => `Join free with ${n}`,
    welcomeBody:
      "Create a free account, finish a short profile, and only then I'll show the free group link.",
    benefit1: "Free account in seconds",
    benefit2: "Short profile (nickname + Telegram + age)",
    benefit3: "Then you see the free group link",
    notVipNote: "Not paid VIP — free community access only.",
    continueCta: "I want free registration",
    accountTitle: "Create free access",
    accountBody:
      "Save your spot with email. Next: complete profile, then the free group link.",
    accountPrivacy: "Only for your access. No spam.",
    createAccessTab: "Create access",
    haveAccountTab: "I have an account",
    nextProfileHint: "Next: complete profile → see group link",
    closeLabel: "Close",
    stepOf: (n, t) => `Step ${n} of ${t}`,
  },
  pt: {
    welcomeEyebrow: "Acesso grátis",
    welcomeTitle: (n) => `Cadastre-se grátis com ${n}`,
    welcomeBody:
      "Crie conta grátis, complete um perfil curto e só então mostro o link do grupo free.",
    benefit1: "Conta grátis em segundos",
    benefit2: "Perfil simples (apelido + Telegram + idade)",
    benefit3: "Depois você vê o link do grupo free",
    notVipNote: "Não é o VIP pago — é o acesso free da comunidade.",
    continueCta: "Quero me cadastrar grátis",
    accountTitle: "Crie seu acesso grátis",
    accountBody:
      "Com o email guardo seu lugar. Depois: perfil e link do grupo free.",
    accountPrivacy: "Só para seu acesso. Sem spam.",
    createAccessTab: "Criar acesso",
    haveAccountTab: "Já tenho conta",
    nextProfileHint: "Próximo: perfil → link do grupo",
    closeLabel: "Fechar",
    stepOf: (n, t) => `Passo ${n} de ${t}`,
  },
  de: {
    welcomeEyebrow: "Kostenloser Zugang",
    welcomeTitle: (n) => `Kostenlos registrieren bei ${n}`,
    welcomeBody:
      "Kostenloses Konto, kurzes Profil — erst danach zeige ich den Free-Gruppen-Link.",
    benefit1: "Kostenloses Konto in Sekunden",
    benefit2: "Kurzes Profil (Name + Telegram + Alter)",
    benefit3: "Danach siehst du den Free-Link",
    notVipNote: "Nicht der bezahlte VIP — nur Free-Community.",
    continueCta: "Kostenlos registrieren",
    accountTitle: "Kostenlosen Zugang erstellen",
    accountBody:
      "Mit E-Mail Platz sichern. Danach Profil — dann Free-Link.",
    accountPrivacy: "Nur für deinen Zugang. Kein Spam.",
    createAccessTab: "Zugang erstellen",
    haveAccountTab: "Ich habe ein Konto",
    nextProfileHint: "Als Nächstes: Profil → Gruppenlink",
    closeLabel: "Schließen",
    stepOf: (n, t) => `Schritt ${n} von ${t}`,
  },
  fr: {
    welcomeEyebrow: "Accès gratuit",
    welcomeTitle: (n) => `Inscris-toi gratuitement avec ${n}`,
    welcomeBody:
      "Compte gratuit, petit profil — seulement après je montre le lien du groupe free.",
    benefit1: "Compte gratuit en quelques secondes",
    benefit2: "Profil court (pseudo + Telegram + âge)",
    benefit3: "Ensuite tu vois le lien free",
    notVipNote: "Pas le VIP payant — accès free communauté.",
    continueCta: "Je m'inscris gratuitement",
    accountTitle: "Crée ton accès gratuit",
    accountBody:
      "Avec l'email je garde ta place. Ensuite: profil, puis le lien free.",
    accountPrivacy: "Uniquement pour ton accès. Pas de spam.",
    createAccessTab: "Créer l'accès",
    haveAccountTab: "J'ai déjà un compte",
    nextProfileHint: "Ensuite: profil → lien du groupe",
    closeLabel: "Fermer",
    stepOf: (n, t) => `Étape ${n} sur ${t}`,
  },
  it: {
    welcomeEyebrow: "Accesso gratuito",
    welcomeTitle: (n) => `Registrati gratis con ${n}`,
    welcomeBody:
      "Account gratis, profilo breve — solo dopo ti mostro il link del gruppo free.",
    benefit1: "Account gratis in secondi",
    benefit2: "Profilo semplice (alias + Telegram + età)",
    benefit3: "Poi vedi il link del gruppo free",
    notVipNote: "Non è il VIP a pagamento — accesso free community.",
    continueCta: "Voglio registrarmi gratis",
    accountTitle: "Crea accesso gratis",
    accountBody:
      "Con l'email ti salvo il posto. Poi profilo e link del gruppo free.",
    accountPrivacy: "Solo per il tuo accesso. Niente spam.",
    createAccessTab: "Crea accesso",
    haveAccountTab: "Ho già un account",
    nextProfileHint: "Dopo: profilo → link del gruppo",
    closeLabel: "Chiudi",
    stepOf: (n, t) => `Passo ${n} di ${t}`,
  },
  nl: {
    welcomeEyebrow: "Gratis toegang",
    welcomeTitle: (n) => `Gratis registreren bij ${n}`,
    welcomeBody:
      "Gratis account, kort profiel — pas daarna zie je de free group link.",
    benefit1: "Gratis account in seconden",
    benefit2: "Kort profiel (alias + Telegram + leeftijd)",
    benefit3: "Daarna zie je de free group link",
    notVipNote: "Geen betaalde VIP — alleen free community.",
    continueCta: "Ik wil gratis registreren",
    accountTitle: "Maak gratis toegang",
    accountBody:
      "Met e-mail bewaar ik je plek. Daarna: profiel, dan free link.",
    accountPrivacy: "Alleen voor je toegang. Geen spam.",
    createAccessTab: "Toegang maken",
    haveAccountTab: "Ik heb al een account",
    nextProfileHint: "Volgende: profiel → group link",
    closeLabel: "Sluiten",
    stepOf: (n, t) => `Stap ${n} van ${t}`,
  },
};

export function freeJoinEntryCopy(
  country?: string | null,
  language?: string | null,
): FreeJoinEntryCopy {
  const fromLang = language
    ? invitacionExclusivaMuxLocaleFromLanguage(language)
    : null;
  const locale =
    fromLang && fromLang !== "en"
      ? fromLang
      : invitacionExclusivaMuxLocaleFromCountry(country);
  return BY_LOCALE[locale] ?? BY_LOCALE.en;
}
